import React, { useEffect, useState } from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import {
    addDays,
    differenceInDays,
    endOfDay,
    endOfMonth,
    isSameDay,
    startOfDay,
    startOfMonth,
    subDays,
} from 'date-fns';
import {
    ChevronLeftIcon,
    ChevronRightIcon,
} from '@heroicons/react/24/outline/index.js';

const MiniCal = ({
    onSelectDay,
    selectedDate,
    viewStart,
    viewEnd,
    events,
    weekStart,
    className='',
    calendarClassName='',
}) => {
    const [dayMarkers, setDayMarkers] = useState({});
    const [maxPerDay, setMaxPerDay] = useState(0);

    useEffect(() => {
        const newDayMarkers = {};
        let startDate = subDays(startOfMonth(startOfDay(selectedDate)), 6);
        const clientEvents = events.filter(
            ev => !!ev.client_id || ev.event_type?.is_class,
        );
        const endDate = addDays(endOfMonth(selectedDate), 6);
        let newMaxPerDay = 0;
        do {
            const dayEnd = endOfDay(startDate);
            const numEvents = clientEvents.filter(
                ev => {
                    // If it's an all day event subtract one day from the end
                    if (ev.event_type?.is_all_day) {
                        return (
                            new Date(ev.start) <= dayEnd &&
                            subDays(new Date(ev.end), 1) >= startDate
                        );
                    } else {
                        return new Date(ev.start) <= dayEnd && new Date(ev.end) >= startDate
                    }
                },
            ).length;
            newMaxPerDay = Math.max(newMaxPerDay, numEvents);
            newDayMarkers[startDate.toISOString()] = numEvents;

            startDate = addDays(startDate, 1);
        } while (startDate <= endDate);

        setDayMarkers(newDayMarkers);
        setMaxPerDay(newMaxPerDay);
    }, [
        JSON.stringify(events),
        startOfMonth(selectedDate).toISOString(),
        endOfMonth(selectedDate).toISOString(),
    ]);

    return (
        <div className={`minical w-[280px] ${className}`}>
            <Calendar
                className={calendarClassName}
                onChange={value => onSelectDay(value)}
                value={selectedDate}
                showFixedNumberOfWeeks={true}
                calendarType={parseInt(weekStart) === 0 ? 'gregory' : 'iso8601'}
                nextLabel={<ChevronRightIcon className="w-4 h-4" />}
                prevLabel={<ChevronLeftIcon className="ml-4 w-4 h-4" />}
                tileClassName={({ activeStartDate, date, view }) => {
                    let className = 'relative ';

                    if (
                        differenceInDays(viewEnd, viewStart) > 1 &&
                        date >= viewStart &&
                        date < viewEnd
                    ) {
                        className += 'currently-viewing-day ';
                    }

                    if (isSameDay(viewStart, date)) {
                        className += 'viewing-start-day ';
                    } else if (isSameDay(subDays(viewEnd, 1), date)) {
                        className += 'viewing-end-day ';
                    }

                    return className;
                }}
                tileContent={arg => {
                    const numOnDay = dayMarkers[arg.date.toISOString()];
                    const busy = numOnDay / Math.max(1, maxPerDay);
                    if (!busy) {
                        return null;
                    }
                    const width = 20 * busy;

                    let bgColor = 'bg-primary-700 ';
                    if (busy < 0.2) bgColor += 'opacity-30';
                    else if (busy < 0.4) bgColor += 'opacity-40';
                    else if (busy < 0.6) bgColor += 'opacity-60';
                    else if (busy < 0.8) bgColor += 'opacity-80';

                    return (
                        <div
                            className="busy-indicator flex items-center justify-center"
                            title={`${numOnDay} events on this day`}
                        >
                            <div
                                className={`h-1 ${bgColor} rounded-full`}
                                style={{
                                    width: width + 'px',
                                }}
                            ></div>
                        </div>
                    );
                }}
            />
        </div>
    );
};

export default MiniCal;
